import * as React from 'react';
import { useCallback } from 'react';

import { Editor, Transforms } from 'slate';
import { HistoryEditor } from 'slate-history';
import { useSlateStatic } from 'slate-react';

import { get as getUser } from 'js/lib/user';

import { useTracker, useVisibilityTracker } from '@coursera/event-pulse/react';

import Button, { TOOLBAR_BUTTON_TYPES } from 'bundles/cml/editor/components/buttons/Button';
import AILayoutDiscoveryTooltip from 'bundles/cml/editor/components/buttons/ai-layout/AILayoutDiscoveryTooltip';
import AILayoutIcon from 'bundles/cml/editor/components/buttons/ai-layout/AILayoutIcon';
import { AI_LAYOUT_PROMPT } from 'bundles/cml/editor/components/buttons/ai-layout/constants';
import { useAILayoutState } from 'bundles/cml/editor/components/buttons/ai-layout/useAILayoutState';
import { useFocusedContext } from 'bundles/cml/editor/context/focusContext';
import { AI_TOOL, BLOCK_TYPES } from 'bundles/cml/shared/constants';
import FloatingTooltip from 'bundles/common/components/Tooltip/FloatingTooltip';
import { useLocalStorage } from 'bundles/common/utils/useLocalStorage';

import _t from 'i18n!nls/cml';

const DEFAULT_TEMPERATURE = 0.1;

const getAIDiscoveryKey = () => `ai-layout-tooltip-dismissed:${getUser?.()?.id ?? 'unknown'}`;

const AILayoutButton = () => {
  const staticEditor = useSlateStatic();
  const { setFocused } = useFocusedContext();

  const [dismissed, setDismissed] = useLocalStorage(getAIDiscoveryKey(), false);
  const track = useTracker();

  const handleClick = useCallback<() => void>(() => {
    track('click_button', {
      button: { name: 'writing_assistant_auto_layout' },
    });

    setFocused(false);
    setDismissed(true);

    HistoryEditor.withoutSaving(staticEditor, () => {
      const start = Editor.start(staticEditor, [0]);
      const end = Editor.end(staticEditor, [staticEditor.children.length - 1]);
      Transforms.setSelection(staticEditor, { anchor: start, focus: end });

      Transforms.wrapNodes(
        staticEditor,
        {
          type: BLOCK_TYPES.AI_ELEMENT,
          aiTool: AI_TOOL.LAYOUT,
          prompt: AI_LAYOUT_PROMPT,
          generating: true,
          temperature: DEFAULT_TEMPERATURE,
          anchorEl: null,
          children: [],
        },
        { mode: 'highest' }
      );

      Editor.addMark(staticEditor, 'selected', true);
      Transforms.collapse(staticEditor, { edge: 'end' });
      Editor.removeMark(staticEditor, 'selected');
    });
  }, [staticEditor, setFocused, setDismissed, track]);

  const ref = useVisibilityTracker<HTMLButtonElement, 'view_button'>('view_button', {
    button: { name: 'writing_assistant_auto_layout' },
  });

  const { active, disabled } = useAILayoutState();

  return (
    <FloatingTooltip
      message=""
      show={!dismissed && !disabled}
      trigger="controlled"
      placement="bottom"
      offset={4}
      renderTooltip={(tooltipProps) => (
        <AILayoutDiscoveryTooltip {...tooltipProps} onDismiss={() => setDismissed(true)} />
      )}
    >
      <Button
        ref={ref}
        onClick={handleClick}
        active={active}
        title={_t('Auto-layout')}
        disabled={disabled}
        type={TOOLBAR_BUTTON_TYPES.insertion}
      >
        <AILayoutIcon height="16px" width="16px" />
      </Button>
    </FloatingTooltip>
  );
};

export default AILayoutButton;
